import * as THREE from 'three';
import { STRUCTURE_STATS, lerpAngle, type MinionState, type StructureState, type Team } from '@moba/shared';
import { instantiateCharacter, type CharacterAsset } from './assets.ts';

import { t } from './i18n.ts';
const TEAM_HEX: Record<Team, number> = { blue: 0x4da3ff, red: 0xff5a5a };
const TEAM_CSS: Record<Team, string> = { blue: '#4da3ff', red: '#ff5a5a' };
const TARGET_HEX = 0xffffff;

/** Barra de vida en sprite (misma estética que la de los héroes, más simple). */
class HpBar {
  readonly sprite: THREE.Sprite;
  private readonly canvas = document.createElement('canvas');
  private readonly texture: THREE.CanvasTexture;
  private lastHp = -1;
  private lastWhite = false;

  constructor(
    private readonly label: string,
    private readonly team: Team,
    width: number,
  ) {
    this.canvas.width = 256;
    this.canvas.height = label ? 56 : 28;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.minFilter = THREE.LinearFilter;
    this.sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: this.texture, transparent: true, depthTest: false, depthWrite: false }),
    );
    this.sprite.scale.set(width, width * (this.canvas.height / 256), 1);
    this.sprite.renderOrder = 30;
  }

  dispose(): void {
    this.texture.dispose();
    this.sprite.material.dispose();
  }

  /** `lastHit` pinta la vida en blanco: el próximo básico del jugador lo remata. */
  draw(hp: number, maxHp: number, lastHit = false): void {
    if (hp === this.lastHp && lastHit === this.lastWhite) return;
    this.lastHp = hp;
    this.lastWhite = lastHit;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    const { width, height } = this.canvas;
    ctx.clearRect(0, 0, width, height);
    const barTop = this.label ? 27 : 2;
    if (this.label) {
      ctx.textBaseline = 'middle';
      ctx.font = '700 15px Bahnschrift, Segoe UI, sans-serif';
      ctx.fillStyle = 'rgba(228,242,255,0.94)';
      ctx.textAlign = 'left';
      ctx.fillText(this.label, 6, 15);
      ctx.textAlign = 'right';
      ctx.font = '600 13px Bahnschrift, Segoe UI, sans-serif';
      ctx.fillStyle = 'rgba(186,212,234,0.82)';
      ctx.fillText(`${Math.ceil(hp)}`, width - 6, 15);
    }
    ctx.fillStyle = 'rgba(4,8,12,0.82)';
    ctx.fillRect(4, barTop, width - 8, 22);
    const ratio = THREE.MathUtils.clamp(hp / maxHp, 0, 1);
    ctx.fillStyle = lastHit ? '#ffffff' : this.team === 'blue' ? '#4da3ff' : '#ff5a5a';
    ctx.fillRect(7, barTop + 3, (width - 14) * ratio, 16);
    ctx.strokeStyle = TEAM_CSS[this.team];
    ctx.lineWidth = 2;
    ctx.strokeRect(4, barTop, width - 8, 22);
    this.texture.needsUpdate = true;
  }
}

/** Geometría con la pose de estatua ya aplicada, por modelo y nombre de malla. */
const statueGeometries = new Map<string, THREE.BufferGeometry>();

/**
 * Congela la pose actual de cada malla con esqueleto en una malla estática.
 * Las estatuas no se animan: como SkinnedMesh, three recalculaba y subía la
 * textura de huesos de las 16 estatuas en cada frame (una subida de textura
 * por estatua y frame, que frenaba la GPU a ráfagas).
 */
function bakeStatuePose(model: THREE.Object3D, cacheKey: string): void {
  model.updateMatrixWorld(true);
  const skinned: THREE.SkinnedMesh[] = [];
  model.traverse((object) => {
    if ((object as THREE.SkinnedMesh).isSkinnedMesh) skinned.push(object as THREE.SkinnedMesh);
  });
  skinned.forEach((mesh, index) => {
    const key = `${cacheKey}#${index}`;
    let geometry = statueGeometries.get(key);
    if (!geometry) {
      geometry = bakeSkinnedGeometry(mesh);
      statueGeometries.set(key, geometry);
    }
    const baked = new THREE.Mesh(geometry, mesh.material);
    baked.name = mesh.name;
    baked.position.copy(mesh.position);
    baked.quaternion.copy(mesh.quaternion);
    baked.scale.copy(mesh.scale);
    baked.visible = mesh.visible;
    mesh.parent?.add(baked);
    mesh.removeFromParent();
  });
}

/** Aplica el skinning en CPU a posiciones y normales (espacio local de la malla). */
function bakeSkinnedGeometry(mesh: THREE.SkinnedMesh): THREE.BufferGeometry {
  mesh.skeleton.update();
  const geometry = mesh.geometry.clone();
  const position = geometry.getAttribute('position');
  const normal = geometry.getAttribute('normal');
  const skinIndex = geometry.getAttribute('skinIndex');
  const skinWeight = geometry.getAttribute('skinWeight');
  const boneMatrices = mesh.skeleton.boneMatrices as Float32Array;
  const vertex = new THREE.Vector3();
  const skinMatrix = new THREE.Matrix4();
  const boneMatrix = new THREE.Matrix4();
  const normalMatrix = new THREE.Matrix3();
  const n = new THREE.Vector3();
  for (let i = 0; i < position.count; i++) {
    vertex.fromBufferAttribute(position, i);
    mesh.applyBoneTransform(i, vertex);
    position.setXYZ(i, vertex.x, vertex.y, vertex.z);
    if (!normal) continue;
    // matriz de skinning ponderada (bindMatrixInverse · Σ w·hueso · bindMatrix) para la normal
    skinMatrix.set(0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
    for (let k = 0; k < 4; k++) {
      const weight = skinWeight.getComponent(i, k);
      if (weight === 0) continue;
      boneMatrix.fromArray(boneMatrices, skinIndex.getComponent(i, k) * 16);
      const e = skinMatrix.elements;
      const b = boneMatrix.elements;
      for (let j = 0; j < 16; j++) e[j] += b[j] * weight;
    }
    skinMatrix.premultiply(mesh.bindMatrixInverse).multiply(mesh.bindMatrix);
    normalMatrix.getNormalMatrix(skinMatrix);
    n.fromBufferAttribute(normal, i).applyMatrix3(normalMatrix).normalize();
    normal.setXYZ(i, n.x, n.y, n.z);
  }
  geometry.deleteAttribute('skinIndex');
  geometry.deleteAttribute('skinWeight');
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

const silver = new THREE.MeshStandardMaterial({ color: 0xc9d1da, metalness: 0.55, roughness: 0.3 });
const stone = new THREE.MeshStandardMaterial({ color: 0x26323f, roughness: 0.85, metalness: 0.1 });

/**
 * Torre o nexo: estatua plateada de un personaje (pose idle congelada) sobre un
 * pedestal con el color del equipo. Las torres muestran su rango cuando el
 * jugador local está cerca.
 */
export class StructureView {
  readonly root = new THREE.Group();
  readonly height: number;

  private readonly statue: THREE.Object3D;
  private readonly band: THREE.MeshStandardMaterial;
  private readonly rangeRing?: THREE.Mesh;
  private readonly rangeMaterial?: THREE.MeshBasicMaterial;
  private readonly targetRing: THREE.Mesh;
  private readonly targetMaterial: THREE.MeshBasicMaterial;
  private readonly hp: HpBar;
  private targeted = false;
  private wasAlive = true;

  constructor(
    readonly structure: StructureState,
    asset: CharacterAsset,
  ) {
    const isNexus = structure.kind === 'nexus';
    const pedestalHeight = isNexus ? 1.0 : 0.6;
    const statueHeight = isNexus ? 3.8 : 2.7;

    const pedestal = new THREE.Mesh(
      new THREE.CylinderGeometry(structure.radius * 0.95, structure.radius * 1.1, pedestalHeight, 24),
      stone,
    );
    pedestal.position.y = pedestalHeight / 2;
    pedestal.castShadow = true;
    pedestal.receiveShadow = true;
    this.root.add(pedestal);

    this.band = new THREE.MeshStandardMaterial({
      color: TEAM_HEX[structure.team],
      emissive: TEAM_HEX[structure.team],
      emissiveIntensity: 0.9,
      roughness: 0.4,
    });
    const band = new THREE.Mesh(
      // sin tapas: evita z-fighting con la tapa del pedestal
      new THREE.CylinderGeometry(structure.radius * 1.0, structure.radius * 1.0, 0.12, 24, 1, true),
      this.band,
    );
    band.position.y = pedestalHeight - 0.06;
    this.root.add(band);

    // estatua: instancia del personaje, pose idle, material plateado
    const instance = instantiateCharacter(asset);
    const idleName = asset.manifest.clips.idle;
    const idle = THREE.AnimationClip.findByName(instance.clips, idleName);
    if (idle) {
      const mixer = new THREE.AnimationMixer(instance.model);
      mixer.clipAction(idle).play();
      mixer.update(0.4);
    }
    bakeStatuePose(instance.model, asset.manifest.model);
    instance.model.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.material = silver;
      mesh.castShadow = true;
    });
    const scale = statueHeight / asset.manifest.targetHeight;
    instance.root.scale.setScalar(scale);
    instance.root.position.y = pedestalHeight;
    this.statue = instance.root;
    this.root.add(this.statue);
    this.height = pedestalHeight + statueHeight;

    const ringGeometry = new THREE.RingGeometry(structure.radius * 1.15, structure.radius * 1.35, 40);
    ringGeometry.rotateX(-Math.PI / 2);
    this.targetMaterial = new THREE.MeshBasicMaterial({
      color: TARGET_HEX,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      side: THREE.DoubleSide, forceSinglePass: true,
    });
    this.targetRing = new THREE.Mesh(ringGeometry, this.targetMaterial);
    this.targetRing.position.y = 0.05;
    this.root.add(this.targetRing);

    {
      const range = STRUCTURE_STATS[structure.kind].attack.range;
      const rangeGeometry = new THREE.RingGeometry(range - 0.12, range, 96);
      rangeGeometry.rotateX(-Math.PI / 2);
      this.rangeMaterial = new THREE.MeshBasicMaterial({
        color: TEAM_HEX[structure.team],
        transparent: true,
        opacity: 0,
        depthWrite: false,
        side: THREE.DoubleSide, forceSinglePass: true,
      });
      this.rangeRing = new THREE.Mesh(rangeGeometry, this.rangeMaterial);
      this.rangeRing.position.y = 0.04;
      this.root.add(this.rangeRing);
    }

    const label = isNexus
      ? t('NEXO')
      : structure.tier === 3
        ? t('TORRE DEL NEXO')
        : t(structure.tier === 1 ? 'TORRE EXTERIOR' : 'TORRE INTERIOR');
    this.hp = new HpBar(label, structure.team, isNexus ? 3.6 : 2.9);
    this.hp.sprite.position.y = this.height + 0.6;
    this.root.add(this.hp.sprite);

    this.root.position.set(structure.pos.x, 0, structure.pos.z);
    this.statue.rotation.y = structure.facing;
  }

  setTargeted(targeted: boolean): void {
    this.targeted = targeted;
  }

  /** `player` es el héroe local (para el anillo de rango). */
  update(dt: number, player: { pos: { x: number; z: number }; team: Team; id: number } | undefined): void {
    const s = this.structure;
    this.statue.rotation.y = lerpAngle(this.statue.rotation.y, s.facing, 1 - Math.exp(-4 * dt));

    if (!s.alive) {
      if (this.wasAlive) {
        this.wasAlive = false;
        this.statue.visible = false;
        this.hp.sprite.visible = false;
        this.band.emissiveIntensity = 0;
        this.band.color.setHex(0x333a42);
        if (this.rangeRing) this.rangeRing.visible = false;
      }
      this.targetMaterial.opacity = 0;
      return;
    }

    this.hp.draw(s.hp, s.maxHp);
    const pulse = 0.5 + 0.5 * Math.sin(performance.now() * 0.006);
    this.targetMaterial.opacity = this.targeted ? 0.6 + pulse * 0.3 : 0;

    if (this.rangeRing && this.rangeMaterial && player) {
      const dx = player.pos.x - s.pos.x;
      const dz = player.pos.z - s.pos.z;
      const dist = Math.hypot(dx, dz);
      const near = dist < STRUCTURE_STATS[s.kind].attack.range + 4;
      const aimedAtPlayer = s.targetId === player.id;
      this.rangeMaterial.color.setHex(aimedAtPlayer ? 0xff3b3b : TEAM_HEX[s.team]);
      const goal = near ? (aimedAtPlayer ? 0.75 : 0.32) : 0;
      this.rangeMaterial.opacity += (goal - this.rangeMaterial.opacity) * Math.min(1, dt * 8);
    }
  }
}

/** Tiempos (s) de la animación de muerte de los minions: caída, inicio del desvanecido y fin. */
const MINION_DEATH = { fall: 0.35, fadeStart: 0.9, total: 2.0 };

/**
 * Materiales transparentes para la animación de muerte, reutilizados por material
 * de origen. No se liberan nunca: si se liberara el último, three destruiría su
 * programa y el siguiente minion en morir recompilaría el shader (tirón).
 */
const fadePool = new Map<THREE.Material, THREE.Material[]>();

function takeFadeMaterial(source: THREE.Material): THREE.Material {
  const copy = fadePool.get(source)?.pop() ?? source.clone();
  copy.transparent = true;
  copy.opacity = 1;
  copy.depthWrite = true;
  return copy;
}

/** Anillo de equipo compartido por radio (todos los minions del mismo tipo). */
const minionRingGeometries = new Map<number, THREE.RingGeometry>();

/** Minion: modelo estático con contoneo al caminar y embestida al atacar. */
export class MinionView {
  readonly root = new THREE.Group();
  /** daño del próximo básico del jugador (0 = aliado o sin jugador): con esa vida o menos la barra va en blanco */
  lastHitDamage = 0;
  readonly height = 1.0;
  private readonly body = new THREE.Group();
  private readonly hp: HpBar;
  private readonly ringMaterial: THREE.MeshBasicMaterial;
  private targeted = false;
  private phase = Math.random() * Math.PI * 2;
  dying = false;
  private deathTime = 0;
  /** material transparente en uso → material original del asset */
  private readonly fadeMaterials = new Map<THREE.Material, THREE.Material>();
  private readonly bounds: THREE.Box3;

  constructor(
    readonly minion: MinionState,
    model: THREE.Group,
  ) {
    const clone = model.clone(true);
    clone.scale.setScalar(0.95);
    this.body.add(clone);
    this.root.add(this.body);
    // caja del modelo en el espacio del cuerpo (root aún en el origen), para apoyarlo al caer
    this.bounds = new THREE.Box3().setFromObject(this.root);

    let ringGeometry = minionRingGeometries.get(minion.radius);
    if (!ringGeometry) {
      ringGeometry = new THREE.RingGeometry(minion.radius * 0.95, minion.radius * 1.35, 28);
      ringGeometry.rotateX(-Math.PI / 2);
      minionRingGeometries.set(minion.radius, ringGeometry);
    }
    this.ringMaterial = new THREE.MeshBasicMaterial({
      color: TEAM_HEX[minion.team],
      transparent: true,
      opacity: 0.4,
      depthWrite: false,
      side: THREE.DoubleSide, forceSinglePass: true,
    });
    const ring = new THREE.Mesh(ringGeometry, this.ringMaterial);
    ring.position.y = 0.04;
    this.root.add(ring);

    this.hp = new HpBar('', minion.team, 0.85);
    this.hp.sprite.position.y = 1.35;
    this.root.add(this.hp.sprite);

    this.root.position.set(minion.pos.x, 0, minion.pos.z);
    this.root.rotation.y = minion.facing;
  }

  setTargeted(targeted: boolean): void {
    this.targeted = targeted;
  }

  /** Arranca la animación de muerte: cae de espaldas, se queda tendido y se desvanece. */
  die(): void {
    if (this.dying) return;
    this.dying = true;
    this.deathTime = 0;
    this.hp.sprite.visible = false;
    this.body.position.set(0, 0, 0);
    this.body.rotation.set(0, 0, 0);
    // el modelo comparte materiales con el asset: se clonan para poder transparentarlos
    this.body.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      const source = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      const cloned = source.map((material) => {
        const copy = takeFadeMaterial(material);
        this.fadeMaterials.set(copy, material);
        return copy;
      });
      mesh.material = Array.isArray(mesh.material) ? cloned : cloned[0];
    });
  }

  /** La animación de muerte terminó y la vista puede retirarse. */
  get finished(): boolean {
    return this.dying && this.deathTime >= MINION_DEATH.total;
  }

  /**
   * Altura a la que hay que subir el cuerpo, girado `angle` sobre X alrededor de los pies,
   * para que su punto más bajo quede en el suelo (si no, el caparazón queda medio enterrado).
   */
  private restingLift(angle: number): number {
    const { min, max } = this.bounds;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    let lowest = Infinity;
    for (const y of [min.y, max.y]) {
      for (const z of [min.z, max.z]) lowest = Math.min(lowest, y * cos - z * sin);
    }
    return Math.max(0, -lowest);
  }

  private updateDeath(dt: number): void {
    this.deathTime += dt;
    const t = this.deathTime;
    const { fall, fadeStart, total } = MINION_DEATH;
    // caída con un pequeño rebote al tocar el suelo
    const f = Math.min(1, t / fall);
    const eased = 1 - (1 - f) ** 3;
    const bounce = f >= 1 ? Math.sin(Math.min(1, (t - fall) / 0.18) * Math.PI) * 0.08 : 0;
    const angle = -(Math.PI / 2) * eased + bounce;
    this.body.rotation.x = angle;
    // se desvanece y se hunde apenas en el suelo
    const fade = t <= fadeStart ? 0 : Math.min(1, (t - fadeStart) / (total - fadeStart));
    this.body.position.y = this.restingLift(angle) - 0.2 * fade;
    for (const material of this.fadeMaterials.keys()) {
      material.opacity = 1 - fade;
      material.depthWrite = fade < 0.05;
    }
    this.ringMaterial.opacity = 0.4 * Math.max(0, 1 - t / fall);
  }

  update(dt: number, alpha: number): void {
    if (this.dying) {
      this.updateDeath(dt);
      return;
    }
    const m = this.minion;
    const x = m.prevPos.x + (m.pos.x - m.prevPos.x) * alpha;
    const z = m.prevPos.z + (m.pos.z - m.prevPos.z) * alpha;
    this.root.position.set(x, 0, z);
    this.root.rotation.y = lerpAngle(this.root.rotation.y, m.facing, 1 - Math.exp(-12 * dt));

    const moving = (m.pos.x - m.prevPos.x) ** 2 + (m.pos.z - m.prevPos.z) ** 2 > 1e-6;
    if (moving) this.phase += dt * 11;
    const waddle = moving ? 1 : 0;
    this.body.position.y = Math.abs(Math.sin(this.phase)) * 0.07 * waddle;
    this.body.rotation.z = Math.sin(this.phase) * 0.14 * waddle;

    // embestida: los primeros ticks tras golpear
    const lunge = m.attackTicks < 8 ? Math.sin((m.attackTicks / 8) * Math.PI) : 0;
    this.body.position.z = lunge * 0.3;
    this.body.rotation.x = lunge * 0.25;

    this.ringMaterial.color.setHex(this.targeted ? TARGET_HEX : TEAM_HEX[m.team]);
    this.ringMaterial.opacity = this.targeted ? 0.85 : 0.4;
    this.hp.draw(m.hp, m.maxHp, m.hp > 0 && m.hp <= this.lastHitDamage);
  }

  dispose(): void {
    this.root.removeFromParent();
    this.hp.dispose();
    this.ringMaterial.dispose();
    // los materiales de la muerte vuelven al pool (ver `fadePool`)
    for (const [copy, source] of this.fadeMaterials) {
      const pool = fadePool.get(source) ?? [];
      pool.push(copy);
      fadePool.set(source, pool);
    }
    this.fadeMaterials.clear();
    // el modelo clonado comparte geometría (y, en vida, material) con el asset: no se libera
  }
}
